import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router';
import {
  ParamListBase,
  useNavigation as useNativeNavigation,
} from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { logInfo } from '@bonanza-social/toolkit/logging';
import { getNavigationHandlers } from '../../utils/registry';
import { Modal } from '../../ModalsManager/ModalsManager';

const useNavigation = <T extends ParamListBase>() => {
  const navigateRouter = useNavigate();
  const navigateNative = useNativeNavigation<StackNavigationProp<T>>();

  const navigate = useCallback(
    (screen_: keyof T, params?: T[typeof screen_]) => {
      const handlers = getNavigationHandlers();
      const screen = params && 'screen' in params ? params.screen : screen_;

      logInfo('Navigation', screen, params);

      const handler = handlers[screen as keyof T];

      if (handler) {
        handler?.(navigateRouter, params);

        return;
      }

      // @ts-expect-error: Fix types
      navigateNative.navigate(screen, params);
    },
    [navigateRouter, navigateNative],
  );

  const goBack = useCallback(() => {
    const openModal = Modal.getCurrentModal();

    if (navigateNative.canGoBack()) {
      navigateNative.goBack();
    } else if (openModal) {
      Modal.close();
    } else {
      navigateRouter(-1);
    }
  }, [navigateNative, navigateRouter]);

  const popToTop = useCallback(() => {
    if (navigateNative.canGoBack() && navigateNative.popToTop) {
      navigateNative.popToTop();
    } else {
      const openModal = Modal.getCurrentModal();

      if (openModal) {
        Modal.close();
      } else {
        navigateRouter('/', { replace: true });
      }
    }
  }, [navigateNative, navigateRouter]);

  const getParent = useCallback(() => {
    const parent = navigateNative.getParent();

    return {
      ...parent,
      goBack: Modal.close,
    };
  }, [navigateNative]);

  const returnObj = useMemo(
    () => ({
      ...navigateNative,
      push: navigate,
      getParent,
      navigate,
      goBack,
      popToTop,
    }),
    [navigate, goBack, getParent, navigateNative, popToTop],
  );

  return returnObj;
};

export default useNavigation;
